import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';

import { useGlobalPermissions } from '../../../query-hooks/global-permissions';
import { SkillChipWithDrawer } from '../../skills/SkillChipWithDrawer';
import type { SkillRow, TableRow } from './SkillsDataGrid';

const sortSkills = (row1: TableRow, row2: TableRow): number => {
    const isSkill = row1.type === 'skill' && row2.type === 'skill';

    if (isSkill) {
        return row1.skill.title.localeCompare(row2.skill.title);
    }

    return 0;
};

const sortTableRows = (row1: TableRow, row2: TableRow): number => {
    const isInSameArea = row1.area === row2.area;

    if (isInSameArea) {
        return sortSkills(row1, row2);
    }

    return 0;
};

const countColumn = (field: string, header: string, value: (tableRow: SkillRow) => number): GridColDef<TableRow> => ({
    disableColumnMenu: true,
    field,
    headerName: header,
    minWidth: 200,
    renderCell: (params: GridRenderCellParams<TableRow>) => {
        const tableRow = params.row;

        if (tableRow.type === 'skill') {
            return value(tableRow);
        }

        return null;
    },
    resizable: false,
    sortable: false,
});

type SkillsColumnsProps = {
    readonly sortable?: boolean;
};

export const useSkillsColumns = ({ sortable = true }: SkillsColumnsProps): GridColDef<TableRow>[] => {
    const { translate } = useContext(ApplicationContext);
    const { data: globalPermissions } = useGlobalPermissions();
    const withLearningOpportunities = globalPermissions?.canSearchLearningOpportunities ?? false;

    const titleColumn: GridColDef<TableRow> = {
        disableColumnMenu: true,
        field: 'title',
        flex: 1,
        headerName: translate('pages.global-search.headlines.skill'),

        renderCell: (params: GridRenderCellParams<TableRow>) => {
            const tableRow = params.row;

            if (tableRow.type === 'skill') {
                return (
                    <Grid>
                        <Box key={tableRow.skill.id} width="auto">
                            <SkillChipWithDrawer
                                label={tableRow.skill.title}
                                skillId={tableRow.skill.id}
                                type={tableRow.skill.skillType}
                            />
                        </Box>
                    </Grid>
                );
            }

            return tableRow.title;
        },
        resizable: false,
        sortable,
        sortComparator: sortTableRows,
        valueGetter: (_value, row) => row,
    };

    const usersColumn = countColumn(
        'usersWithThisSkill',
        translate('pages.global-search.headlines.employees'),
        (tableRow) => tableRow.skill.usersWithThisSkill,
    );
    const jobsColumn = countColumn(
        'jobsWithThisSkill',
        translate('pages.global-search.headlines.jobs'),
        (tableRow) => tableRow.skill.jobsWithThisSkill,
    );
    if (withLearningOpportunities) {
        const learningOpportunitiesColumn = countColumn(
            'learningOpportunitiesWithThisSkill',
            translate('pages.global-search.headlines.learning-opportunities'),
            (tableRow) => tableRow.skill.learningOpportunitiesWithThisSkill,
        );

        return [titleColumn, usersColumn, jobsColumn, learningOpportunitiesColumn];
    }

    return [titleColumn, usersColumn, jobsColumn];
};
